"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  Add,
  Edit,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
  Visibility,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  TableContainer,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  InputLabel,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
   Used everywhere a scrollable area appears: the table
   container and all modal scroll areas.
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   RESPONSE HELPERS
========================================================= */

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.salary_structures)) return data.salary_structures;

  return [];
};

/* =========================================================
   EMPTY FORM
========================================================= */

const emptyForm = {
  employee: "",
  basic_salary: "",
  hra: "",
  da: "",
  medical_allowance: "",
  travel_allowance: "",
  other_allowance: "",
  pf: "",
  esi: "",
  professional_tax: "",
  other_deduction: "",
};

/* =========================================================
   COMPONENT
========================================================= */

function SalaryStructure() {
  const theme = useTheme();

  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     USER
  ======================================================= */

  const [currentUser, setCurrentUser] = useState(null);

  const [organizationId, setOrganizationId] = useState("");

  /* =======================================================
     DATA
  ======================================================= */

  const [salaryStructures, setSalaryStructures] = useState([]);

  const [employees, setEmployees] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [employeeLoading, setEmployeeLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [statusSaving, setStatusSaving] = useState(false);

  /* Shows the table loader from first render until the
     initial fetch completes. */

  const [initialLoading, setInitialLoading] = useState(true);

  /* =======================================================
     SEARCH — server-side `search` param (per Swagger),
     triggered by Enter or the Search button
  ======================================================= */

  const [searchInput, setSearchInput] = useState("");

  const [appliedSearch, setAppliedSearch] = useState("");

  /* =======================================================
     STATUS FILTER (client-side)
     NOTE: the list endpoint only returns ACTIVE salary
     structures, so the "Inactive" option filters the
     already-active records client-side.
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [filterStatus, setFilterStatus] = useState("");

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     ADD / EDIT
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  const [formErrors, setFormErrors] = useState({});

  /* =======================================================
     EMPLOYEE MODAL
  ======================================================= */

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [employeeSearch, setEmployeeSearch] = useState("");

  /* =======================================================
     STATUS
  ======================================================= */

  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const [statusItem, setStatusItem] = useState(null);

  /* =======================================================
     DETAIL
  ======================================================= */

  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [detailItem, setDetailItem] = useState(null);

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     STYLES
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: inputBackground,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-error fieldset": { borderColor: theme.palette.error.main },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.8,
    },
    "& .MuiInputLabel-root": { color: textPrimary },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: theme.palette.error.main,
    },
  };

  const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     ERROR HELPERS
  ======================================================= */

  const formatFieldLabel = (field) => {
    const labels = {
      basic_salary: "Basic Salary",
      hra: "HRA",
      da: "DA",
      medical_allowance: "Medical Allowance",
      travel_allowance: "Travel Allowance",
      other_allowance: "Other Allowance",
      pf: "PF",
      esi: "ESI",
      professional_tax: "Professional Tax",
      other_deduction: "Other Deduction",
      employee: "Employee",
      branch: "Branch",
      organization: "Organization",
    };

    return (
      labels[field] ||
      field
        ?.replaceAll("_", " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const extractBackendErrors = (error) => {
    const responseData = error?.response?.data;

    const backendErrors = responseData?.errors;

    if (!backendErrors || typeof backendErrors !== "object") {
      return {};
    }

    const normalizedErrors = {};

    Object.entries(backendErrors).forEach(([field, messages]) => {
      if (Array.isArray(messages)) {
        normalizedErrors[field] = messages
          .map((message) =>
            typeof message === "string"
              ? message
              : message?.message || String(message)
          )
          .join(" ");
      } else if (typeof messages === "string") {
        normalizedErrors[field] = messages;
      } else if (messages && typeof messages === "object") {
        normalizedErrors[field] = Object.values(messages)
          .flat(Infinity)
          .map((message) => String(message))
          .join(" ");
      }
    });

    return normalizedErrors;
  };

  const getErrorMessage = (error) => {
    if (error?.response?.status === 401) {
      return "Unauthorized. Please login again.";
    }

    const backendErrors = extractBackendErrors(error);

    if (Object.keys(backendErrors).length > 0) {
      return Object.entries(backendErrors)
        .map(([field, message]) => `${formatFieldLabel(field)}: ${message}`)
        .join(" | ");
    }

    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    if (error?.response?.data) {
      const data = error.response.data;

      if (typeof data === "string") {
        return data;
      }

      return Object.values(data)
        .flat(Infinity)
        .map((item) => String(item))
        .join(" ");
    }

    return error?.message || "Something went wrong.";
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     FORMAT MONEY
  ======================================================= */

  const formatMoney = (value) => {
    const number = Number(value || 0);

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* =======================================================
     LABEL HELPERS
  ======================================================= */

  const getEmployeeLabel = (employee) => {
    return (
      employee?.name ||
      employee?.employee_name ||
      employee?.full_name ||
      employee?.employee_code ||
      `Employee ${employee?.id}`
    );
  };

  const getEmployeeBranchId = (employee) => {
    return (
      employee?.branch_id || employee?.branch?.id || employee?.branch || ""
    );
  };

  /* =======================================================
     FETCH EMPLOYEES (scoped to global branch)
  ======================================================= */

  const fetchEmployees = useCallback(async () => {
    if (!organizationId) return;

    try {
      setEmployeeLoading(true);

      const params = { organization_id: organizationId };

      if (selectedBranchId) {
        params.branch_id = selectedBranchId;
      }

      const response = await api.get("/staff/employees/", { params });

      setEmployees(getArray(response));
    } catch (error) {
      console.error("Employee API Error:", error);

      setEmployees([]);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setEmployeeLoading(false);
    }
  }, [organizationId, selectedBranchId, showSnackbar]);

  /* =======================================================
     FETCH SALARY STRUCTURES
     GET /hr/salary-structures/
     params: organization, branch, search
     (branch comes from the global Branch context)
  ======================================================= */

  const fetchSalaryStructures = useCallback(
    async (options = {}) => {
      if (!organizationId) return;

      const search =
        options.search !== undefined ? options.search : appliedSearch;

      const branch =
        options.branch !== undefined ? options.branch : selectedBranchId;

      setLoading(true);

      try {
        const params = { organization: organizationId };

        if (branch) {
          params.branch = branch;
        }

        if (String(search).trim()) {
          params.search = String(search).trim();
        }

        const response = await api.get("/hr/salary-structures/", { params });

        setSalaryStructures(getArray(response));

        setPage(0);
      } catch (error) {
        console.error("Salary Structure API Error:", error);

        setSalaryStructures([]);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);
      }
    },
    [organizationId, selectedBranchId, appliedSearch, showSnackbar]
  );

  /* =======================================================
     LOAD AUTH USER
  ======================================================= */

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      setInitialLoading(false);

      showSnackbar("Logged-in user not found.", "error");

      return;
    }

    setCurrentUser(user);

    const orgId =
      user.organization_id ?? user.organization?.id ?? user.organization ?? "";

    setOrganizationId(String(orgId));
  }, [showSnackbar]);

  /* =======================================================
     INITIAL DATA — runs whenever the global branch changes
  ======================================================= */

  useEffect(() => {
    if (!organizationId) {
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);

    const loadData = async () => {
      await fetchEmployees();

      await fetchSalaryStructures({ branch: selectedBranchId });

      setInitialLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     SEARCH SUBMIT / CLEAR — server-side `search` param
  ======================================================= */

  const handleSearchSubmit = () => {
    setAppliedSearch(searchInput);

    fetchSalaryStructures({ search: searchInput });
  };

  const handleClearSearch = () => {
    setSearchInput("");

    setAppliedSearch("");

    fetchSalaryStructures({ search: "" });
  };

  /* =======================================================
     STATUS FILTER (client-side) + PAGINATION
  ======================================================= */

  const filteredStructures = useMemo(() => {
    if (!filterStatus) {
      return salaryStructures;
    }

    return salaryStructures.filter((item) =>
      filterStatus === "active"
        ? item.is_active === true
        : item.is_active === false
    );
  }, [salaryStructures, filterStatus]);

  const displayedStructures = filteredStructures.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =======================================================
     OPEN CREATE
     Branch is fixed to the globally selected branch.
  ======================================================= */

  const handleOpenCreate = () => {
    if (!selectedBranchId) {
      showSnackbar(
        "Please select a branch from the global branch selector first.",
        "error"
      );
      return;
    }

    setEditMode(false);

    setSelectedId(null);

    setFormErrors({});

    setFormData({ ...emptyForm });

    setEmployeeSearch("");

    setOpenDialog(true);
  };

  /* =======================================================
     OPEN EDIT
  ======================================================= */

  const handleOpenEdit = (item) => {
    setEditMode(true);

    setSelectedId(item.id);

    setFormErrors({});

    setFormData({
      employee: item.employee ?? "",
      basic_salary: item.basic_salary ?? "",
      hra: item.hra ?? "",
      da: item.da ?? "",
      medical_allowance: item.medical_allowance ?? "",
      travel_allowance: item.travel_allowance ?? "",
      other_allowance: item.other_allowance ?? "",
      pf: item.pf ?? "",
      esi: item.esi ?? "",
      professional_tax: item.professional_tax ?? "",
      other_deduction: item.other_deduction ?? "",
    });

    setEmployeeSearch("");

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);

    setSelectedId(null);

    setEditMode(false);

    setFormErrors({});

    setFormData({ ...emptyForm });
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }

      const updated = { ...prev };

      delete updated[name];

      return updated;
    });

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {
    const errors = {};

    if (!organizationId) {
      errors.organization = "Organization is required.";
    }

    if (!selectedBranchId) {
      errors.branch = "Please select a branch from the global selector.";
    }

    if (!formData.employee) {
      errors.employee = "Employee is required.";
    }

    if (
      formData.basic_salary === "" ||
      Number(formData.basic_salary) < 0 ||
      Number.isNaN(Number(formData.basic_salary))
    ) {
      errors.basic_salary = "Basic salary must be a valid amount.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      showSnackbar(Object.values(errors).join(" | "), "error");

      return false;
    }

    return true;
  };

  /* =======================================================
     BUILD PAYLOAD
     Per Swagger: money fields are decimal STRINGS,
     organization / branch / employee are integers.
     Branch comes from the global selector.
  ======================================================= */

  const buildPayload = () => {
    const money = (value) => (value === "" ? "0.00" : String(value));

    return {
      basic_salary: money(formData.basic_salary),
      hra: money(formData.hra),
      da: money(formData.da),
      medical_allowance: money(formData.medical_allowance),
      travel_allowance: money(formData.travel_allowance),
      other_allowance: money(formData.other_allowance),
      pf: money(formData.pf),
      esi: money(formData.esi),
      professional_tax: money(formData.professional_tax),
      other_deduction: money(formData.other_deduction),
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      employee: Number(formData.employee),
    };
  };

  /* =======================================================
     APPLY BACKEND FORM ERRORS
  ======================================================= */

  const applyBackendErrors = (error) => {
    const backendErrors = extractBackendErrors(error);

    if (Object.keys(backendErrors).length === 0) {
      return false;
    }

    setFormErrors(backendErrors);

    showSnackbar(
      Object.entries(backendErrors)
        .map(([field, errorMessage]) => `${formatFieldLabel(field)}: ${errorMessage}`)
        .join(" | "),
      "error"
    );

    return true;
  };

  /* =======================================================
     CREATE — POST /hr/salary-structures/
  ======================================================= */

  const handleCreate = async () => {
    if (!validateForm()) return;

    setSaving(true);

    setFormErrors({});

    try {
      await api.post("/hr/salary-structures/", buildPayload());

      showSnackbar("Salary structure created successfully.", "success");

      setOpenDialog(false);

      setFormData({ ...emptyForm });

      await fetchSalaryStructures();
    } catch (error) {
      console.error("Create Salary Structure Error:", error);

      if (!applyBackendErrors(error)) {
        showSnackbar(getErrorMessage(error), "error");
      }
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     UPDATE — PUT /hr/salary-structures/{id}/update/
  ======================================================= */

  const handleUpdate = async () => {
    if (!validateForm()) return;

    if (!selectedId) return;

    setSaving(true);

    setFormErrors({});

    try {
      await api.put(
        `/hr/salary-structures/${selectedId}/update/`,
        buildPayload()
      );

      showSnackbar("Salary structure updated successfully.", "success");

      setOpenDialog(false);

      setSelectedId(null);

      await fetchSalaryStructures();
    } catch (error) {
      console.error("Update Salary Structure Error:", error);

      if (!applyBackendErrors(error)) {
        showSnackbar(getErrorMessage(error), "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  /* =======================================================
     EMPLOYEES FOR SELECTED BRANCH (global selector)
  ======================================================= */

  const branchEmployees = useMemo(() => {
    if (!selectedBranchId) {
      return [];
    }

    return employees.filter(
      (employee) =>
        String(getEmployeeBranchId(employee)) === String(selectedBranchId)
    );
  }, [employees, selectedBranchId]);

  const filteredEmployees = useMemo(() => {
    const search = employeeSearch.trim().toLowerCase();

    if (!search) {
      return branchEmployees;
    }

    return branchEmployees.filter((employee) => {
      const name = String(
        employee.name ||
          employee.employee_name ||
          employee.full_name ||
          ""
      ).toLowerCase();

      const code = String(employee.employee_code || "").toLowerCase();

      const department = String(
        employee.department_name || employee.department || ""
      ).toLowerCase();

      const designation = String(
        employee.designation_name || employee.designation || ""
      ).toLowerCase();

      return (
        name.includes(search) ||
        code.includes(search) ||
        department.includes(search) ||
        designation.includes(search)
      );
    });
  }, [branchEmployees, employeeSearch]);

  const selectedEmployee = employees.find(
    (employee) => String(employee.id) === String(formData.employee)
  );

  /* =======================================================
     EMPLOYEE MODAL
  ======================================================= */

  const handleOpenEmployeeModal = () => {
    if (!selectedBranchId) {
      showSnackbar(
        "Please select a branch from the global branch selector first.",
        "error"
      );

      return;
    }

    setEmployeeSearch("");

    setEmployeeModalOpen(true);
  };

  const handleCloseEmployeeModal = () => {
    setEmployeeModalOpen(false);
  };

  const handleSelectEmployee = (employee) => {
    const employeeBranch = getEmployeeBranchId(employee);

    if (
      selectedBranchId &&
      employeeBranch &&
      String(employeeBranch) !== String(selectedBranchId)
    ) {
      showSnackbar(
        "Employee does not belong to the selected branch.",
        "error"
      );

      return;
    }

    setFormErrors((prev) => {
      const updated = { ...prev };

      delete updated.employee;

      delete updated.basic_salary;

      return updated;
    });

       setFormData((prev) => {
      const isDifferentEmployee =
        String(prev.employee) !== String(employee.id);

      return {
        ...prev,
        employee: employee.id,
        basic_salary: isDifferentEmployee
          ? employee.salary ?? ""
          : prev.basic_salary,
      };
    });

    setEmployeeModalOpen(false);
  };

  /* =======================================================
     STATUS
     PATCH /hr/salary-structures/{id}/status/
     body: { is_active }
  ======================================================= */

  const handleOpenStatus = (item) => {
    setStatusItem(item);

    setStatusModalOpen(true);
  };

  const handleCloseStatus = () => {
    if (statusSaving) return;

    setStatusItem(null);

    setStatusModalOpen(false);
  };

  const handleConfirmStatus = async () => {
    if (!statusItem) return;

    const newStatus = !statusItem.is_active;

    setStatusSaving(true);

    try {
      await api.patch(`/hr/salary-structures/${statusItem.id}/status/`, {
        is_active: newStatus,
      });

      setSalaryStructures((prev) =>
        prev.map((item) =>
          item.id === statusItem.id ? { ...item, is_active: newStatus } : item
        )
      );

      showSnackbar(
        `Salary structure ${
          newStatus ? "activated" : "deactivated"
        } successfully.`,
        "success"
      );

      setStatusModalOpen(false);

      setStatusItem(null);
    } catch (error) {
      console.error("Salary Structure Status Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setStatusSaving(false);
    }
  };

  /* =======================================================
     DETAIL — GET /hr/salary-structures/{id}/
  ======================================================= */

  const handleOpenDetail = async (item) => {
    setDetailItem(item);

    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailItem(null);

    setDetailModalOpen(false);
  };

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

  const handleApplyFilter = () => {
    setPage(0);

    setFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setFilterStatus("");

    setSearchInput("");

    setAppliedSearch("");

    setPage(0);

    setFilterModalOpen(false);

    fetchSalaryStructures({ search: "" });
  };

  /* =======================================================
     REFRESH
  ======================================================= */

  const handleRefresh = async () => {
    await fetchEmployees();

    await fetchSalaryStructures();
  };

  /* =======================================================
     MONEY FIELD
  ======================================================= */

  const renderMoneyField = (name, label) => {
    const hasError = Boolean(formErrors[name]);

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        name={name}
        type="number"
        value={formData[name]}
        onChange={handleFormChange}
        error={hasError}
        helperText={hasError ? formErrors[name] : ""}
        inputProps={{ min: 0, step: "0.01" }}
        sx={{
          ...textFieldStyles,
          "& .MuiFormHelperText-root": {
            color: theme.palette.error.main,
            marginLeft: 0,
            marginTop: 0.5,
          },
        }}
      />
    );
  };

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — server-side `search` param, Enter/button
            triggered */}

        <TextField
          size="small"
          variant="outlined"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSearchSubmit();
            }
          }}
          placeholder="Search employee, code, branch..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 380 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* SEARCH BUTTON */}

          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearchSubmit}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Search
          </Button>

          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Add Salary Structure
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          {/* MAIN TABLE — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table stickyHeader sx={{ minWidth: 1250 }}>
              <TableHead>
                                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Employee</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Basic Salary</TableCell>

                  <TableCell>Gross Salary</TableCell>

                  <TableCell>Deduction</TableCell>

                  <TableCell>Net Salary</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading || initialLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading salary structures...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedStructures.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No salary structures found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Try changing your search or filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedStructures.map((item, index) => (
                    <TableRow key={item.id} hover>
                      {/* S.NO */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* EMPLOYEE */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography fontWeight={600} sx={{ color: textPrimary }}>
                          {item.employee_name || "-"}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: textPrimary }}
                        >
                          {item.employee_code || "-"}
                        </Typography>
                      </TableCell>

                      {/* BRANCH */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          sx={{ color: textPrimary, fontWeight: 500 }}
                        >
                          {item.branch_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* BASIC */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          sx={{ color: textPrimary, fontWeight: 500 }}
                        >
                          ₹{formatMoney(item.basic_salary)}
                        </Typography>
                      </TableCell>

                      {/* GROSS */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          sx={{ color: textPrimary, fontWeight: 600 }}
                        >
                          ₹{formatMoney(item.gross_salary)}
                        </Typography>
                      </TableCell>

                      {/* DEDUCTION */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          sx={{ color: textPrimary, fontWeight: 500 }}
                        >
                          ₹{formatMoney(item.total_deduction)}
                        </Typography>
                      </TableCell>

                      {/* NET */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          fontWeight={700}
                          sx={{ color: theme.palette.success.main }}
                        >
                          ₹{formatMoney(item.net_salary)}
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.2,
                            py: 0.45,
                            borderRadius: "999px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: item.is_active
                              ? theme.palette.success.dark
                              : theme.palette.error.dark,
                            backgroundColor: item.is_active
                              ? alpha(theme.palette.success.main, isDark ? 0.18 : 0.12)
                              : alpha(theme.palette.error.main, isDark ? 0.18 : 0.12),
                            border: `1px solid ${
                              item.is_active
                                ? alpha(theme.palette.success.main, 0.35)
                                : alpha(theme.palette.error.main, 0.35)
                            }`,
                          }}
                        >
                          {item.is_active ? "Active" : "Inactive"}
                        </Box>
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {/* VIEW */}

                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetail(item)}
                              sx={{
                                color: theme.palette.info.main,
                                border: `1px solid ${alpha(
                                  theme.palette.info.main,
                                  0.3
                                )}`,
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    isDark ? 0.15 : 0.08
                                  ),
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* EDIT */}

                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(item)}
                              sx={{
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )}`,
                                borderRadius: 1.5,
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    isDark ? 0.15 : 0.08
                                  ),
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* STATUS */}

                          <Tooltip
                            title={item.is_active ? "Deactivate" : "Activate"}
                          >
                            <Switch
                              size="small"
                              checked={item.is_active === true}
                              onChange={() => handleOpenStatus(item)}
                              sx={{
                                ml: 0.25,
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: theme.palette.success.main,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: theme.palette.success.main,
                                    opacity: 0.5,
                                  },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side */}

          <TablePagination
            component="div"
            count={filteredStructures.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              borderTop: `1px solid ${borderColor}`,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          FILTER DIALOG
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Filter Salary Structures
          </Typography>

          <IconButton
            onClick={() => setFilterModalOpen(false)}
            sx={{ color: textPrimary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 1,
            }}
          >
            <Typography variant="body2" color="text.primary">
              Branch is applied automatically from the global branch selector.
            </Typography>

            {/* STATUS */}

            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, color: textPrimary }}>
                Status
              </Typography>

              <Select
                value={filterStatus}
                onChange={(event) => setFilterStatus(event.target.value)}
                displayEmpty
                sx={selectStyles}
                MenuProps={menuProps}
              >
                <MenuItem value="">All Status</MenuItem>

                <MenuItem value="active">Active</MenuItem>

                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Note: the salary structure list endpoint returns only active
            structures, so the "Inactive" filter applies to the loaded
            records only.
          </Alert>
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleResetFilter}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleApplyFilter}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          ADD / EDIT DIALOG
      =================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {editMode ? "Edit Salary Structure" : "Add Salary Structure"}
          </Typography>

          <IconButton
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              color: textPrimary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* ORGANIZATION (read-only) */}

            {/* <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                ORGANIZATION
              </Typography>

              <TextField
                fullWidth
                size="small"
                value={
                  currentUser?.organization_name ||
                  currentUser?.organization?.name ||
                  currentUser?.organization ||
                  `Organization ${organizationId || ""}`
                }
                InputProps={{ readOnly: true }}
                sx={textFieldStyles}
              />
            </Box> */}

            {/* BRANCH — from the global selector, read-only */}

            {/* <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                BRANCH
              </Typography>

              <TextField
                fullWidth
                size="small"
                value={
                  salaryStructures.find(
                    (item) => String(item.id) === String(selectedId)
                  )?.branch_name ||
                  (selectedBranchId
                    ? `Selected Branch (ID: ${selectedBranchId})`
                    : "No branch selected")
                }
                InputProps={{ readOnly: true }}
                sx={{
                  ...textFieldStyles,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: formErrors.branch
                      ? theme.palette.error.main
                      : borderColor,
                  },
                }}
              />

              <Typography
                variant="caption"
                color="text.primary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Branch is taken from the global branch selector.
              </Typography>

              {formErrors.branch && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: theme.palette.error.main }}
                >
                  {formErrors.branch}
                </Typography>
              )}
            </Box> */}

            {/* EMPLOYEE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                EMPLOYEE
              </Typography>

              <Box
                onClick={handleOpenEmployeeModal}
                sx={{
                  minHeight: 40,
                  border: `1px solid ${
                    formErrors.employee
                      ? theme.palette.error.main
                      : borderColor
                  }`,
                  borderRadius: 2,
                  backgroundColor: inputBackground,
                  display: "flex",
                  alignItems: "center",
                  px: 1.7,
                  cursor: selectedBranchId ? "pointer" : "not-allowed",
                  opacity: selectedBranchId ? 1 : 0.65,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: selectedBranchId
                      ? theme.palette.primary.main
                      : borderColor,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: textPrimary,
                    fontWeight: formData.employee ? 600 : 400,
                  }}
                >
                  {selectedEmployee
                    ? `${getEmployeeLabel(selectedEmployee)}${
                        selectedEmployee.employee_code
                          ? ` (${selectedEmployee.employee_code})`
                          : ""
                      }`
                    : selectedBranchId
                    ? "Select Employee"
                    : "Select Branch First"}
                </Typography>
              </Box>

              {formErrors.employee && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: theme.palette.error.main,
                  }}
                >
                  {formErrors.employee}
                </Typography>
              )}

              {selectedBranchId && !formErrors.employee && (
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{ display: "block", mt: 0.7 }}
                >
                  Only employees belonging to the selected branch will be
                  shown.
                </Typography>
              )}
            </Box>

            {/* EARNINGS */}

            <Typography variant="subtitle2" fontWeight={700}>
              Earnings
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              {renderMoneyField("basic_salary", "Basic Salary *")}

              {renderMoneyField("hra", "HRA")}

              {renderMoneyField("da", "DA")}

              {renderMoneyField("medical_allowance", "Medical Allowance")}

              {renderMoneyField("travel_allowance", "Travel Allowance")}

              {renderMoneyField("other_allowance", "Other Allowance")}
            </Box>

            {/* DEDUCTIONS */}

            <Typography variant="subtitle2" fontWeight={700}>
              Deductions
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              {renderMoneyField("pf", "PF")}

              {renderMoneyField("esi", "ESI")}

              {renderMoneyField("professional_tax", "Professional Tax")}

              {renderMoneyField("other_deduction", "Other Deduction")}
            </Box>
          </Box>
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{ textTransform: "none", borderRadius: 2 }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {saving ? "Saving..." : editMode ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          EMPLOYEE SELECT DIALOG
      =================================================== */}

      <Dialog
        open={employeeModalOpen}
        onClose={handleCloseEmployeeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Select Employee
            </Typography>

            <Typography variant="caption" color="text.primary">
              Employees of the globally selected branch
            </Typography>
          </Box>

          <IconButton onClick={handleCloseEmployeeModal} sx={{ color: textPrimary }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search employee, code, department..."
            value={employeeSearch}
            onChange={(event) => setEmployeeSearch(event.target.value)}
            sx={{ mb: 2, ...textFieldStyles }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* EMPLOYEE TABLE — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar>
            <TableContainer
              sx={{
                maxHeight: 450,
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
              }}
            >
              <Table stickyHeader sx={{ minWidth: 800 }}>
                <TableHead>
                                   <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        backgroundColor: theme.palette.primary.main,
                        color: "#FFFFFF",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell>Employee</TableCell>

                    <TableCell>Code</TableCell>

                    <TableCell>Department</TableCell>

                    <TableCell>Designation</TableCell>

                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {employeeLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={28} />

                        <Typography
                          variant="body2"
                          sx={{ mt: 1, color: textPrimary }}
                        >
                          Loading employees...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Typography fontWeight={600}>No employees found</Typography>

                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ mt: 0.5 }}
                        >
                          No employee belongs to the selected branch or matches
                          your search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((employee) => (
                      <TableRow key={employee.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography fontWeight={600} sx={{ color: textPrimary }}>
                            {getEmployeeLabel(employee)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography sx={{ color: textPrimary, fontWeight: 500 }}>
                            {employee.employee_code || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {employee.department_name || employee.department || "-"}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {employee.designation_name || employee.designation || "-"}
                        </TableCell>

                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSelectEmployee(employee)}
                            sx={{ textTransform: "none", borderRadius: 2 }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTableScrollbar>
        </StyledDialogContent>
      </Dialog>

      {/* ===================================================
          STATUS DIALOG
      =================================================== */}

      <Dialog
        open={statusModalOpen}
        onClose={handleCloseStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle fontWeight={700}>
          Confirm Status Change
        </DialogTitle>

        <StyledDialogContent>
          <Typography sx={{ color: textPrimary }}>
            Are you sure you want to{" "}
            {statusItem?.is_active ? "deactivate" : "activate"} this salary
            structure?
          </Typography>

          {statusItem && (
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              {statusItem.employee_name || `Employee ${statusItem.employee}`}
            </Typography>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseStatus}
            disabled={statusSaving}
            sx={{ textTransform: "none" }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirmStatus}
            disabled={statusSaving}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {statusSaving ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DETAIL DIALOG
      =================================================== */}

      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetail}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Salary Structure Details
          </Typography>

          <IconButton onClick={handleCloseDetail} sx={{ color: textPrimary }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          {detailItem && (
            <Box>
              {/* EMPLOYEE + BRANCH */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mb: 3,
                }}
              >
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${borderColor}`,
                    boxShadow: "none",
                  }}
                >
                  <Typography variant="caption" color="text.primary">
                    Employee
                  </Typography>

                  <Typography fontWeight={700} sx={{ color: textPrimary }}>
                    {detailItem.employee_name || "-"}
                  </Typography>

                  <Typography variant="body2" color="text.primary">
                    {detailItem.employee_code || "-"}
                  </Typography>
                </Paper>

                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${borderColor}`,
                    boxShadow: "none",
                  }}
                >
                  <Typography variant="caption" color="text.primary">
                    Branch
                  </Typography>

                  <Typography fontWeight={700} sx={{ color: textPrimary }}>
                    {detailItem.branch_name || "-"}
                  </Typography>
                </Paper>
              </Box>

              {/* EARNINGS */}

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Earnings
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {[
                  ["Basic Salary", detailItem.basic_salary],
                  ["HRA", detailItem.hra],
                  ["DA", detailItem.da],
                  ["Medical Allowance", detailItem.medical_allowance],
                  ["Travel Allowance", detailItem.travel_allowance],
                  ["Other Allowance", detailItem.other_allowance],
                ].map(([label, value]) => (
                  <Paper
                    key={label}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                      boxShadow: "none",
                    }}
                  >
                    <Typography variant="caption" color="text.primary">
                      {label}
                    </Typography>

                    <Typography fontWeight={600} sx={{ color: textPrimary }}>
                      ₹{formatMoney(value)}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* DEDUCTIONS */}

              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mt: 3, mb: 1 }}
              >
                Deductions
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {[
                  ["PF", detailItem.pf],
                  ["ESI", detailItem.esi],
                  ["Professional Tax", detailItem.professional_tax],
                  ["Other Deduction", detailItem.other_deduction],
                ].map(([label, value]) => (
                  <Paper
                    key={label}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                      boxShadow: "none",
                    }}
                  >
                    <Typography variant="caption" color="text.primary">
                      {label}
                    </Typography>

                    <Typography fontWeight={600} sx={{ color: textPrimary }}>
                      ₹{formatMoney(value)}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {/* TOTALS */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 1.5,
                  mt: 3,
                }}
              >
                {[
                  ["Gross Salary", detailItem.gross_salary],
                  ["Total Deduction", detailItem.total_deduction],
                  ["Net Salary", detailItem.net_salary],
                ].map(([label, value]) => (
                  <Paper
                    key={label}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                      boxShadow: "none",
                    }}
                  >
                    <Typography variant="caption" color="text.primary">
                      {label}
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: textPrimary }}
                    >
                      ₹{formatMoney(value)}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetail} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={closeSnackbar}
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SalaryStructure;